import type {
  ReservationSlotTemplate,
  ReservationSlotTemplateInput,
} from '../../../domain/reservationSlots';
import type { ReservationSlotTemplateRepository } from '../../../application/reservationSlots/ports/ReservationSlotTemplateRepository';
import { reservationSlotSettingsRepositoryMock } from './ReservationSlotSettingsRepositoryMock';
import { mockDelay, createIdSequencer } from './mockUtils';

/**
 * 初期テンプレート（要件定義のシーダーに準拠）
 */
const SEED_TEMPLATES: ReservationSlotTemplate[] = [
  {
    id: 'tpl-1',
    name: 'ピル標準',
    intervalMinutes: 15,
    patternType: 'single',
    slotsPerInterval: 2,
    slotsA: ['pill', 'pill'],
    slotsB: null,
  },
  {
    id: 'tpl-2',
    name: 'フルカテゴリ',
    intervalMinutes: 15,
    patternType: 'alternate',
    slotsPerInterval: 3,
    slotsA: ['pill', 'pill', 'skin'],
    slotsB: ['pill', 'pill', 'wellness_clinic'],
  },
];

const nextTemplateId = createIdSequencer('tpl');

/**
 * 解放枠テンプレート Repository のモック実装
 *
 * @description
 * テンプレートをメモリ上に保持しCRUDを提供する。
 * 削除時は基本設定モックへ参照有無を問い合わせ、参照中なら実APIと同様に拒否する。
 *
 * @implements {ReservationSlotTemplateRepository}
 */
export class ReservationSlotTemplateRepositoryMock implements ReservationSlotTemplateRepository {
  private templates: ReservationSlotTemplate[] = SEED_TEMPLATES.map((t) => ({ ...t }));

  /**
   * テンプレートを同期的に取得する（生成モックが枠計算に使用）
   */
  findByIdSync(id: string): ReservationSlotTemplate | undefined {
    return this.templates.find((t) => t.id === id);
  }

  async findAll(): Promise<ReservationSlotTemplate[]> {
    await mockDelay();
    return this.templates.map((t) => ({ ...t }));
  }

  async create(input: ReservationSlotTemplateInput): Promise<ReservationSlotTemplate> {
    await mockDelay();
    const template: ReservationSlotTemplate = {
      id: nextTemplateId(),
      name: input.name,
      intervalMinutes: input.intervalMinutes,
      patternType: input.patternType,
      slotsPerInterval: input.slotsPerInterval,
      slotsA: [...input.slotsA],
      slotsB: input.patternType === 'alternate' && input.slotsB ? [...input.slotsB] : null,
    };
    this.templates.push(template);
    return { ...template };
  }

  async update(id: string, input: ReservationSlotTemplateInput): Promise<ReservationSlotTemplate> {
    await mockDelay();
    const index = this.templates.findIndex((t) => t.id === id);
    if (index === -1) {
      throw new Error('対象のテンプレートが見つかりません');
    }
    const updated: ReservationSlotTemplate = {
      id,
      name: input.name,
      intervalMinutes: input.intervalMinutes,
      patternType: input.patternType,
      slotsPerInterval: input.slotsPerInterval,
      slotsA: [...input.slotsA],
      slotsB: input.patternType === 'alternate' && input.slotsB ? [...input.slotsB] : null,
    };
    this.templates[index] = updated;
    return { ...updated };
  }

  async delete(id: string): Promise<void> {
    await mockDelay();
    if (reservationSlotSettingsRepositoryMock.isTemplateReferenced(id)) {
      throw new Error('このテンプレートは基本設定または個別上書きから参照中のため削除できません');
    }
    this.templates = this.templates.filter((t) => t.id !== id);
  }
}

export const reservationSlotTemplateRepositoryMock = new ReservationSlotTemplateRepositoryMock();
